import type { BlockHighlightRect } from "./blockHighlightRect";

/**
 * 列表拖拽落点：
 * - **目标行**仅由指针 **Y** 落在哪条 li 的竖直区间决定；同一 Y 命中父子多条时，取 **Lexical 列表嵌套最深** 的一条（子级行）。
 * - **before/after** 仍相对该行的竖向区域（上缘/下缘/中线）划分。
 */
export type ListLocalDropIntent = {
  targetKey: string;
  placement: "before" | "after";
};

/**
 * 指针 Y 落在 [top, top+height] 内的所有候选行；嵌套时父子区间重叠，取 depth 最大者 = 当前 Y 下的实际子级行。
 */
export function pickListItemRowByPointerY(
  pointerY: number,
  targetRects: BlockHighlightRect[],
  getListItemDepth: (itemKey: string) => number,
): BlockHighlightRect | null {
  const inBand = targetRects.filter((r) => pointerY >= r.top && pointerY <= r.top + r.height);
  if (inBand.length === 0) return null;
  return inBand.reduce((best, r) => {
    const dr = getListItemDepth(r.key);
    const db = getListItemDepth(best.key);
    if (dr !== db) return dr > db ? r : best;
    // 同深度时取更「矮」的一条，避免大块容器与内行歧义
    return r.height < best.height ? r : best;
  });
}

/** 仅用相对目标行的 Y：上缘 before、下缘 after、中间用行中线分 before/after */
export function resolveVerticalPlacementInRect(pointerY: number, r: BlockHighlightRect): "before" | "after" {
  const ty = pointerY - r.top;
  const h = Math.max(1, r.height);

  const edgeY = Math.max(10, Math.min(h * 0.28, 22));
  const inTop = ty < edgeY;
  const inBottom = ty > h - edgeY;

  if (inTop) return "before";
  if (inBottom) return "after";
  return ty < h / 2 ? "before" : "after";
}

/** 指针 Y 落在所有行竖直区间之外时：用中线规则选最近一条 */
export function fallbackRowBeforeAfter(
  pointerY: number,
  targetRects: BlockHighlightRect[],
): { rect: BlockHighlightRect; placement: "before" | "after" } {
  const sorted = [...targetRects].sort((a, b) => a.top - b.top);
  let listIdx = sorted.findIndex((r) => pointerY < r.top + r.height / 2);
  if (listIdx < 0) listIdx = sorted.length - 1;
  const row = sorted[listIdx];
  const atListEnd = listIdx === sorted.length - 1 && pointerY >= row.top + row.height / 2;
  return {
    rect: row,
    placement: atListEnd ? "after" : "before",
  };
}

/**
 * @param targetRects 已排除被拖项及其子树
 * @param getListItemDepth 与编辑器一致的「列表项在树中的层数」，用于 Y 重叠时选最深行
 */
export function resolveListItemDropIntent(
  pointerY: number,
  targetRects: BlockHighlightRect[],
  getListItemDepth: (itemKey: string) => number,
): ListLocalDropIntent | null {
  if (targetRects.length === 0) return null;

  const hit = pickListItemRowByPointerY(pointerY, targetRects, getListItemDepth);
  if (hit) {
    return {
      targetKey: hit.key,
      placement: resolveVerticalPlacementInRect(pointerY, hit),
    };
  }

  const fb = fallbackRowBeforeAfter(pointerY, targetRects);
  return {
    targetKey: fb.rect.key,
    placement: fb.placement,
  };
}
